import { useSuspenseQueries } from '@tanstack/react-query';
import React from 'react';

import type * as api from '@gitbook/api-client';
import { convertBillingAmountToAnalyticsAmount } from '@gitbook/app-analytics';
import * as analytics from '@gitbook/app-analytics/client';
import {
    BillingQueryParams,
    type NavigationScreenSiteState,
    resolveNavigationScreenToURL,
} from '@gitbook/app-navigation';
import { getSiteProductDefinition, isSiteTypeDowngrade } from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Button,
    type ButtonProps,
    DialogBody,
    DialogFooter,
    Heading,
    LoadingIndicator,
} from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';
import { assertIsDefined, assertNever, type MandateProps } from '@gitbook/util-ts';

import { APIErrorBoundary, SiteBillingInvoicePreview } from '../../../../../Components';
import { useNavigationState, useSetAnalyticsScreen } from '../../../../../navigation';
import {
    apiClient,
    apiQuery,
    assertOrganizationWithBilling,
    useAssertCurrentOrganizationId,
    useRedirectToStripe,
    useSiteContext,
} from '../../../../../state';
import {
    assertIsSiteUpgradeModalNewPricing,
    type SiteUpgradeModalStateBillingNew,
    useSiteUpgradeModal,
} from '..';
import { useUpgradeStatusModal } from '../useUpgradeStatusModal';
import { SiteUpgradeModalStepDowngrade } from './SiteUpgradeModalStepDowngrade';

type PlanUpdate = api.OperationSpec<'previewSitePlanUpdate'>['ResponseBody'];
type NewUpgradePlanUpdate = Extract<PlanUpdate, { type: 'checkout' }>;
type PreviewNewUpgradePlanUpdate = MandateProps<NewUpgradePlanUpdate, 'invoice'>;

function isPreviewableNewUpgradePlanUpdate(
    update: PlanUpdate
): update is PreviewNewUpgradePlanUpdate {
    return update.type === 'checkout' && update.invoice !== undefined;
}

export function SiteUpgradeModalStepBilling({
    upgradeState,
}: {
    upgradeState: SiteUpgradeModalStateBillingNew;
}) {
    assertIsDefined(upgradeState);

    return (
        <div className="flex flex-col items-start justify-start gap-4">
            <APIErrorBoundary
                fallback={{
                    403: (
                        <>
                            <DialogBody>You don't have permission to do that.</DialogBody>
                        </>
                    ),
                }}
            >
                <SiteUpgradeModalBilling upgradeState={upgradeState} />
            </APIErrorBoundary>
        </div>
    );
}

function SiteUpgradeModalBilling({
    upgradeState,
}: {
    upgradeState: SiteUpgradeModalStateBillingNew;
}) {
    const { open: openPlanModal, site } = useSiteUpgradeModal();
    const organizationId = useAssertCurrentOrganizationId();
    assertIsDefined(site);

    const [{ data: checkout }, { data: organization }] = useSuspenseQueries({
        queries: [
            apiQuery('previewSitePlanUpdate', {
                organizationId,
                siteId: site.id,
                plan: upgradeState.type,
            }),
            apiQuery('getOrganizationById', {
                organizationId,
            }),
        ],
    });

    const footer = useChangePlanModalBillingFooter({ checkout, upgradeState });

    if (checkout.type === 'trial-available') {
        throw new Error('trial-available should be handled in Plans step');
    }

    const body = (() => {
        // The user is an org admin, so they should have access to billing information.
        assertOrganizationWithBilling(organization);

        if (isPreviewableNewUpgradePlanUpdate(checkout)) {
            return (
                <ChangePlanModalBodyNewBilling checkout={checkout} billing={organization.billing} />
            );
        }

        if (checkout.type === 'go-ahead' && isSiteTypeDowngrade(site.type, upgradeState.type)) {
            return <SiteUpgradeModalStepDowngrade from={site.type} to={upgradeState.type} />;
        }
    })();

    return (
        <>
            {body}
            {footer.label && <Alert kind="info" size="small" details={footer.label} />}
            <DialogFooter>
                <Button
                    title="Back"
                    label="Back"
                    onPress={() => openPlanModal(site, { ...upgradeState, tab: 'plans' })}
                    kind="secondary"
                />
                <Button {...footer.nextButtonProps} />
            </DialogFooter>
        </>
    );
}

function ChangePlanModalBodyNewBilling({
    checkout,
    billing,
}: {
    checkout: PreviewNewUpgradePlanUpdate;
    billing: api.OrganizationBilling;
}) {
    const { site, analyticsContext } = useSiteContext();
    const { upgradeState } = useSiteUpgradeModal();
    assertIsDefined(upgradeState);
    assertIsSiteUpgradeModalNewPricing(upgradeState);

    // Track the screen along with billing information.
    useSetAnalyticsScreen(
        analytics.createSiteEvent(
            { id: analyticsContext.site, organization: analyticsContext.organization },
            {
                screen: 'site_plan_modal_billing',
                reason: upgradeState.reason,
                plan: upgradeState.type,
                interval: billing.interval,
                amount: convertBillingAmountToAnalyticsAmount(checkout.invoice.amount),
                amountDueToday: convertBillingAmountToAnalyticsAmount(
                    checkout.invoice.amountDueToday
                ),
                customerBalance: convertBillingAmountToAnalyticsAmount(
                    checkout.invoice.customerBalance
                ),
                remainingCustomerBalance: convertBillingAmountToAnalyticsAmount(
                    checkout.invoice.remainingCustomerBalance
                ),
            }
        ),
        [
            analyticsContext,
            upgradeState.reason,
            checkout.type,
            billing.interval,
            checkout.invoice.amount,
            checkout.invoice.amountDueToday,
            checkout.invoice.customerBalance,
            checkout.invoice.remainingCustomerBalance,
        ]
    );

    const { title: fromTitle } = getSiteProductDefinition(site.type);
    const { title: toTitle } = getSiteProductDefinition(upgradeState.type);

    return (
        <DialogBody className="flex max-w-112 flex-col items-start justify-start gap-4">
            <Heading>Switch plan</Heading>
            <SiteBillingInvoicePreview
                billing={billing}
                invoice={checkout.invoice}
                details={
                    <>
                        Your site will be switched from <strong>{fromTitle}</strong> to{' '}
                        <strong>{toTitle}</strong>. Here is an overview of your account balance and
                        upcoming invoice:
                    </>
                }
            />
        </DialogBody>
    );
}

function useChangePlanModalBillingFooter({
    checkout,
    upgradeState,
}: {
    checkout: PlanUpdate;
    upgradeState: SiteUpgradeModalStateBillingNew;
}): {
    nextButtonProps: ButtonProps;
    label?: string;
} {
    const { organizationId, site } = useSiteContext();
    const { close } = useSiteUpgradeModal();
    const [loading, wrapLoading] = useLoadingState();
    const redirectToStripe = useRedirectToStripe();
    const { title } = getSiteProductDefinition(upgradeState.type);
    const { screen: currentScreen } = useNavigationState();
    const { openUpgradeStatusModal } = useUpgradeStatusModal();
    const isDowngrade = isSiteTypeDowngrade(site.type, upgradeState.type);

    if (checkout.type === 'trial-available') {
        throw new Error('trial-available should be handled in Plans step');
    }

    return React.useMemo(() => {
        if (checkout.type === 'go-ahead') {
            return {
                label: isDowngrade
                    ? undefined
                    : `You'll need to pay for this site when publishing.`,
                nextButtonProps: {
                    title: isDowngrade ? `Downgrade to ${title}` : 'Confirm',
                    label: isDowngrade ? `Downgrade to ${title}` : 'Confirm',
                    kind: 'primary',
                    disabled: loading,
                    leadingIcon: loading ? LoadingIndicator : undefined,
                    onPress: () =>
                        wrapLoading(async () => {
                            // Site isn't published, perform a simple update.
                            await apiClient.sites.updateSitePlan(organizationId, site.id, {
                                plan: upgradeState.type,
                                reason: upgradeState.reason,
                            });
                            close();
                        }),
                },
            };
        }

        if (checkout.type === 'checkout') {
            return {
                nextButtonProps: {
                    title: `Upgrade to ${title}`,
                    label: `Upgrade to ${title}`,
                    kind: 'upgrade',
                    leadingIcon: loading ? LoadingIndicator : Icon.Upgrade,
                    disabled: loading,
                    onPress: () =>
                        wrapLoading(async () => {
                            // Checkout the site to upgrade the plan.
                            const { data: response } = await apiClient.sites.updateSitePlan(
                                organizationId,
                                site.id,
                                {
                                    plan: upgradeState.type,
                                    reason: upgradeState.reason,
                                },
                                {
                                    successReturnURL: resolveNavigationScreenToURL({
                                        ...(currentScreen as NavigationScreenSiteState),
                                        billing: BillingQueryParams.UpgradeSuccess,
                                    }),
                                    cancelReturnURL: resolveNavigationScreenToURL({
                                        ...(currentScreen as NavigationScreenSiteState),
                                    }),
                                }
                            );

                            switch (response.type) {
                                case 'checkout':
                                    close();
                                    await redirectToStripe(response.sessionId);
                                    break;
                                case 'success':
                                    // Customer subscription was upgraded without the need to checkout.
                                    close();
                                    openUpgradeStatusModal();
                                    break;
                                default:
                                    assertNever(response);
                            }
                        }),
                },
            };
        }

        assertNever(checkout);
    }, [
        checkout,
        title,
        loading,
        wrapLoading,
        organizationId,
        site.id,
        isDowngrade,
        upgradeState,
        close,
        currentScreen,
        redirectToStripe,
        openUpgradeStatusModal,
    ]);
}
